/**
 * <share-button> custom element.
 *
 * Progressive enhancement for the share control rendered by snippets/share-button.liquid
 * (used by the article "share" block). The markup already ships a usable readonly URL
 * field inside a <details> disclosure; this script layers behaviour on top:
 *
 *   - When the native Web Share API is available, the standalone button is revealed and
 *     tapping it opens the OS share sheet (the disclosure fallback is hidden).
 *   - Otherwise the disclosure is used as-is and the copy button writes the URL to the
 *     clipboard, revealing a success message and a close button. If programmatic copy is
 *     unavailable, the readonly input is selected so the user can copy manually.
 *
 * Loaded as a classic, deferred script (see share-button.liquid), so it must not use
 * ES module `import` syntax.
 */
if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends HTMLElement {
      constructor() {
        super();

        this.details = this.querySelector('details');
        this.elements = {
          // The first <button> is the standalone native-share trigger (hidden by default).
          shareButton: this.querySelector('button'),
          closeButton: this.querySelector('.share-button__close'),
          copyButton: this.querySelector('.share-button__copy'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input'),
        };

        this.urlToShare = this.elements.urlInput?.value || document.location.href;

        if (typeof navigator.share === 'function') {
          this.setupNativeShare();
        } else {
          this.setupFallback();
        }
      }

      /** Use the OS-level share sheet when the Web Share API is supported. */
      setupNativeShare() {
        const { shareButton } = this.elements;
        this.details?.setAttribute('hidden', '');
        if (!shareButton) return;

        shareButton.classList.remove('hidden');
        shareButton.addEventListener('click', () => {
          // Swallow AbortError etc. when the user dismisses the share sheet.
          navigator.share({ url: this.urlToShare, title: document.title }).catch(() => {});
        });
      }

      /** Copy-to-clipboard disclosure fallback for browsers without Web Share. */
      setupFallback() {
        this.elements.copyButton?.addEventListener('click', () => this.copy());
        this.elements.closeButton?.addEventListener('click', () => this.close());

        // Reset the transient success state whenever the disclosure is collapsed.
        const { details } = this;
        if (details) {
          details.addEventListener('toggle', () => {
            if (!details.open) this.resetMessage();
          });
        }
      }

      copy() {
        if (typeof navigator.clipboard?.writeText === 'function') {
          navigator.clipboard
            .writeText(this.urlToShare)
            .then(() => this.showSuccess())
            .catch(() => this.selectInput());
        } else {
          this.selectInput();
        }
      }

      /** Select the URL so the user can copy it manually when programmatic copy fails. */
      selectInput() {
        const { urlInput } = this.elements;
        if (!urlInput) return;
        urlInput.focus();
        urlInput.select();
      }

      showSuccess() {
        const { successMessage, closeButton } = this.elements;
        if (successMessage) {
          successMessage.textContent = this.dataset.successMessage || 'Link copied!';
          successMessage.classList.remove('hidden');
        }
        closeButton?.classList.remove('hidden');
      }

      resetMessage() {
        const { successMessage, closeButton } = this.elements;
        if (successMessage) {
          successMessage.textContent = '';
          successMessage.classList.add('hidden');
        }
        closeButton?.classList.add('hidden');
      }

      close() {
        this.resetMessage();
        if (this.details) this.details.open = false;
      }
    }
  );
}
